<template>
  <router-link class="standings-list-player-parts" :to="standingsPlayer.link">
    <div class="standings-list-player-parts__player">
      <div class="standings-list-player-parts__player-rank" v-text="standingsPlayer.rank" />

      <player-card-parts
        class="standings-list-player-parts__player-data"
        :name="setPlayerName(standingsPlayer.name)"
        :team="standingsPlayer.teamName"
        :img="standingsPlayer.playerPictureImagePath"
      />
    </div>

    <div class="standings-list-player-parts__total-pts">
      <span
        class="standings-list-player-parts__total-pts__label"
        v-text="$tc('StandingsPage.TopPage.totalPts')"
      />
      <span class="standings-list-player-parts__total-pts__points">{{
        standingsPlayer.totalPts
      }}</span>
    </div>

    <svg
      class="standings-list-player-parts__arrow"
      width="18"
      height="18"
      viewBox="0 0 18 18"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M6.03906 1.89844L11.8623 7.72166C12.5457 8.40508 12.5457 9.51312 11.8623 10.1965L6.03906 16.0198L6.03906 13.8984L10.8016 9.13587C10.8993 9.03824 10.8993 8.87995 10.8016 8.78232L6.03906 4.01976L6.03906 1.89844Z"
        fill="black"
      />
    </svg>
  </router-link>
</template>

<script lang="ts">
import { defineComponent, PropType } from '@vue/composition-api'
import PlayerCardParts from '@/components/common/card/PlayerCardParts.vue'
import { Multilingual } from '@/store/stores/collectionModule/documents/GeneralTypes'
import I18n from '@/locales/I18n'

/**
 * Standings選手一覧表示用データの型
 */
export type StandingsPlayerDataType = {
  name: Multilingual
  teamName: string
  playerPictureImagePath: string
  rank: string
  rankNum: number
  carNo: string
  carNoNum: number
  totalPts: number
  link: string
}

/**
 * Standings: 一覧用 選手パーツ
 */
export default defineComponent({
  name: 'StandingsListPlayerParts',
  components: {
    PlayerCardParts,
  },
  props: {
    standingsPlayer: {
      type: Object as PropType<StandingsPlayerDataType>,
      required: true,
    },
  },
  methods: {
    /**
     * 選手名を返す
     * @param name
     */
    setPlayerName(name: Multilingual): string {
      return name[I18n.locale] ?? ''
    },
  },
})
</script>

<style lang="scss" scoped>
.standings-list-player-parts {
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  justify-content: flex-start;
  gap: min(#{convertPXtoVW(10)}, 10px);
  width: 100%;
  max-width: 800px;
  padding: min(#{convertPXtoVW(8)}, 8px) min(#{convertPXtoVW(12)}, 12px)
    min(#{convertPXtoVW(9)}, 9px) min(#{convertPXtoVW(6)}, 6px);
  border-bottom: 1px solid $black10;

  &__player {
    display: flex;
    flex-flow: row nowrap;
    align-items: center;
    justify-content: flex-start;
    gap: min(#{convertPXtoVW(5)}, 5px);
    width: calc(100% - min(#{convertPXtoVW(118)}, 118px));

    &-rank {
      width: min(#{convertPXtoVW(31)}, 31px);
      font-weight: 500;
      font-size: min(#{convertPXtoVW(18)}, 18px);
      line-height: 1.33;
      text-align: center;
      letter-spacing: 0.02em;
      color: $black;
    }

    &-data {
      width: calc(100% - min(#{convertPXtoVW(36)}, 36px));
    }
  }

  &__total-pts {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: 2px 0 0;
    width: min(#{convertPXtoVW(80)}, 80px);
    height: 46px;
    background: $white;
    border: 1px solid $black10;
    border-radius: 6px;
    text-align: center;
    font-weight: 500;
    color: $black;

    &__label {
      font-size: min(#{convertPXtoVW(10)}, 10px);
      line-height: 1.2;
      letter-spacing: 0.04em;
    }

    &__points {
      font-size: min(#{convertPXtoVW(14)}, 14px);
      line-height: 1.43;
      letter-spacing: 0.02em;
    }
  }

  &__arrow {
    width: min(#{convertPXtoVW(18)}, 18px);
  }
}
</style>

<style lang="scss">
.standings-list-player-parts {
  .player-card-parts {
    gap: min(#{convertPXtoVW(8)}, 8px);

    &__data {
      width: calc(100% - min(#{convertPXtoVW(56)}, 56px));

      &__name {
        font-size: min(#{convertPXtoVW(12)}, 12px);
        text-overflow: ellipsis;
        overflow: hidden;
        white-space: nowrap;
      }

      &__team {
        font-size: min(#{convertPXtoVW(10)}, 10px);
        text-overflow: ellipsis;
        overflow: hidden;
        white-space: nowrap;
      }
    }
  }
}
</style>
